import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { HelmetProvider } from 'react-helmet-async';
import axios from 'axios';
import Navbar from './components/Navbar';
import ResizeObserverWrapper from './components/ResizeObserverWrapper';

// Pages
import Home from './pages/Home';
import Presentation from './pages/Presentation';
import Scolarite from './pages/Scolarite';
import ScolariteMaternel from './pages/ScolariteMaternel';
import Login from './pages/Login';
import Registration from './pages/Registration';
import SecretaryLogin from './pages/SecretaryLogin';
import GalleryJeux from './pages/GalleryJeux';
import GalleryVie from './pages/GalleryVie';
import GalleryExtras from './pages/GalleryExtras';
import JeuxEducatifs from './pages/JeuxEducatifs';
import VieAcademique from './pages/VieAcademique';
import ActivitesExtrascolaires from './pages/ActivitesExtrascolaires';
import Activites from './pages/Activites';
import Blog from './pages/Blog';
import BlogDetail from './pages/BlogDetail';
import ActivityDetail from './pages/ActivityDetail';
import BlogGallery from './pages/BlogGallery';
import ActivityGallery from './pages/ActivityGallery';
import RentreeScolaire from './pages/RentreeScolaire';
import ProgrammeActivites from './pages/ProgrammeActivites';
import SortieDecouverte from './pages/SortieDecouverte';
import HomepageManagement from './pages/secretary/HomepageManagement';
import DynamicPagesManagement from './pages/secretary/DynamicPagesManagement';
import StudentDashboard from './pages/StudentDashboard';
import StudentPaymentPage from './pages/StudentPaymentPage';
import StudentPaymentReturn from './pages/StudentPaymentReturn';
import StudentTimetablePage from './pages/StudentTimetablePage';
import StudentSchedule from './pages/StudentSchedule';
import ChooseTrimester from './pages/ChooseTrimester';
import MyReportCard from './pages/MyReportCard';
import StudentReportCard from './pages/StudentReportCard';
import SecretaryDashboard from './pages/SecretaryDashboard';
import Students from './pages/secretary/Students';
import StudentDetails from './pages/secretary/StudentDetails';
import Classes from './pages/secretary/Classes';
import Teachers from './pages/secretary/Teachers';
import Payments from './pages/secretary/Payments';
import Arrears from './pages/secretary/Arrears';
import Settings from './pages/secretary/Settings';
import Subjects from './pages/secretary/Subjects';
import GestionEleves from './pages/GestionEleves';
import EventsPage from './pages/secretary/EventsPage';
import PublicEventPage from './pages/secretary/PublicEventPage';
import ClassEventSelectionPage from './pages/secretary/ClassEventSelectionPage';
import ClassEventCreationPage from './pages/secretary/ClassEventCreationPage';
import PrivateEventPage from './pages/secretary/PrivateEventPage';
import TimetableSelectionPage from './pages/secretary/TimetableSelectionPage';
import ClassTimetablePage from './pages/secretary/ClassTimetablePage';
import ReportCardsClasses from './pages/ReportCardsClasses';
import ReportCardsStudents from './pages/ReportCardsStudents';
import Cantine from './pages/secretary/Cantine';
import MediaManagementPage from './pages/secretary/MediaManagementPage';
import RolesManagement from './pages/secretary/RolesManagement';
import Garderie from './pages/secretary/Garderie';
import History from './pages/secretary/History';
import InscrptionPre from './pages/InscrptionPre';
import TeacherDashboard from './pages/TeacherDashboard';
import ParentDashboard from './pages/ParentDashboard';
import ParentChildProfile from './pages/ParentChildProfile';
import DOMErrorTest from './components/DOMErrorTest';
import ErrorBoundary from './components/ErrorBoundary';
import ScheduleTab from './pages/ScheduleTab';

// Components
import PWAInstallPrompt from './components/PWAInstallPrompt';
import ServiceWorkerManager from './components/ServiceWorkerManager';

// Utils
import { registerServiceWorker } from './utils/pwaUtils';

// Theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#1780c2',
    },
    secondary: {
      main: '#f50057',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
  },
});

const RegistrationWrapper = () => {
  const navigate = useNavigate();
  // Fournit une fonction onClose simple qui redirige l'utilisateur.
  return <Registration onClose={() => navigate('/login')} />;
};

function AppContent() {
  const location = useLocation();
  const hideNavbarOn = ['/teacher/dashboard', '/student/dashboard', '/student/payment', '/student/report-card', '/student/report-card/:trimester', '/student/choose-trimester', '/parent/dashboard'];

  // Masquer la navbar aussi sur la page emploi du temps étudiant et parent
const hideNavbar =
    location.pathname === '/' ||
    location.pathname === '/presentation' ||
    location.pathname === '/scolarite' ||
    location.pathname === '/scolarite-maternel' ||
    location.pathname === '/activites' ||
    location.pathname === '/blog' ||
    location.pathname === '/rentree-scolaire' ||
    location.pathname === '/programme-activites' ||
    location.pathname === '/sortie-decouverte' ||
    location.pathname.startsWith('/blog/') ||
    location.pathname.startsWith('/activites/') ||
    location.pathname.startsWith('/blog-gallery/') ||
    location.pathname.startsWith('/activity-gallery/') ||
    hideNavbarOn.includes(location.pathname) ||
    location.pathname.startsWith('/secretary/') ||
    location.pathname.startsWith('/student/schedule/') ||
    location.pathname.startsWith('/student/report-card/') ||
    location.pathname.startsWith('/parent/child/') ||
    location.pathname === '/jeux-educatifs' ||
    location.pathname === '/vie-academique' ||
    location.pathname === '/activites-extrascolaires' ||
    location.pathname === '/gallery/jeux' ||
    location.pathname === '/gallery/vie' ||
    location.pathname === '/gallery/extras';

  return (
    <div className="App">
      {!hideNavbar && <Navbar />}
      <Routes>
        {/* Routes publiques */}
        <Route path="/" element={<Home />} />
        <Route path="/presentation" element={<Presentation />} />
        <Route path="/scolarite" element={<Scolarite />} />
        <Route path="/scolarite-maternel" element={<ScolariteMaternel />} />
        <Route path="/activites" element={<Activites />} />
        <Route path="/activites/:id" element={<ActivityDetail />} />
        <Route path="/activity-gallery/:sectionId" element={<ActivityGallery />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:id" element={<BlogDetail />} />
        <Route path="/blog-gallery/:category" element={<BlogGallery />} />
        <Route path="/rentree-scolaire" element={<RentreeScolaire />} />
        <Route path="/programme-activites" element={<ProgrammeActivites />} />
        <Route path="/sortie-decouverte" element={<SortieDecouverte />} />
        <Route path="/login" element={<Login />} />
        <Route path="/registration" element={<RegistrationWrapper />} />
        <Route path="/secretary-login" element={<SecretaryLogin />} />
        <Route path="/dom-error-test" element={<DOMErrorTest />} />
        {/* Galleries */}
        <Route path="/gallery/jeux" element={<GalleryJeux />} />
        <Route path="/gallery/vie" element={<GalleryVie />} />
        <Route path="/gallery/extras" element={<GalleryExtras />} />
        
        {/* Pages d'activités */}
        <Route path="/jeux-educatifs" element={<JeuxEducatifs />} />
        <Route path="/vie-academique" element={<VieAcademique />} />
        <Route path="/activites-extrascolaires" element={<ActivitesExtrascolaires />} />
        <Route path="/secretary/homepage-management" element={<HomepageManagement />} />
        <Route path="/secretary/dynamic-pages" element={<DynamicPagesManagement />} />

        {/* Routes Étudiant */}
        <Route path="/student/dashboard" element={<StudentDashboard />} />
        <Route path="/student/payment" element={<StudentPaymentPage />} />
        <Route path="/student/payment-return" element={<StudentPaymentReturn />} />
        <Route path="/student/timetable" element={<StudentTimetablePage />} />
        <Route path="/student/schedule/:studentId" element={<StudentSchedule />} />
        <Route path="/student/choose-trimester" element={<ChooseTrimester />} />
        <Route path="/student/report-card/:trimester" element={<MyReportCard />} />
        <Route path="/student/report-card" element={<MyReportCard />} />
        <Route path="/student/report-card/:studentId/:classId" element={<StudentReportCard />} />

        {/* Routes Secrétaire */}
        <Route path="/secretary/dashboard" element={<SecretaryDashboard />} />
        <Route path="/secretary/students" element={<Students />} />
        <Route path="/secretary/students/:id" element={<StudentDetails />} />
        <Route path="/secretary/classes" element={<Classes />} />
        <Route path="/secretary/teachers" element={<Teachers />} />
        <Route path="/secretary/payments" element={<Payments />} />
        <Route path="/secretary/arrears" element={<Arrears />} />
        <Route path="/secretary/settings" element={<Settings />} />
        <Route path="/secretary/subjects" element={<Subjects />} />
        <Route path="/secretary/gestion-eleves" element={<GestionEleves />} />
        <Route path="/secretary/events" element={<EventsPage />} />
        <Route path="/secretary/events/public" element={<PublicEventPage />} />
        <Route path="/secretary/events/class" element={<ClassEventSelectionPage />} />
        <Route path="/secretary/events/class/:classId" element={<ClassEventCreationPage />} />
        <Route path="/secretary/events/private" element={<PrivateEventPage />} />
        <Route path="/secretary/timetables" element={<TimetableSelectionPage />} />
        <Route path="/secretary/timetables/:classId" element={<ClassTimetablePage />} />
        <Route path="/secretary/report-cards" element={<ReportCardsClasses />} />
        <Route path="/secretary/report-cards/:classId" element={<ReportCardsStudents />} />
        <Route path="/secretary/report-cards/:classId/:studentId" element={<StudentReportCard />} />
        <Route path="/secretary/cantine" element={<Cantine />} />
        <Route path="/secretary/media" element={<MediaManagementPage />} />
        <Route path="/secretary/roles" element={<RolesManagement />} />
        <Route path="/secretary/garderie" element={<Garderie />} />
        <Route path="/secretary/history" element={<History />} />
        <Route path="/secretary/inscription-pre" element={<InscrptionPre onClose={() => window.history.length > 1 ? window.history.back() : window.location.replace('/secretary/dashboard')} />} />
        <Route path="/teacher/dashboard" element={<TeacherDashboard />} />
        <Route path="/parent/dashboard" element={<ParentDashboard />} />
        <Route path="/parent/child/:childId" element={<ParentChildProfile />} />
      </Routes>
    </div>
  );
}

function App() {
  useEffect(() => {
    // Le Service Worker est géré par le composant ServiceWorkerManager
    // registerServiceWorker();
    
    // Configurer axios pour utiliser le token s'il existe
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }, []);

  return (
    <ErrorBoundary>
      <ResizeObserverWrapper>
        <HelmetProvider>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <Router>
              <ServiceWorkerManager>
                <AppContent />
                
                {/* Composant PWA Install Prompt */}
                <PWAInstallPrompt />
              </ServiceWorkerManager>
            </Router>
          </ThemeProvider>
        </HelmetProvider>
      </ResizeObserverWrapper>
    </ErrorBoundary>
  );
}

export default App;
